import React from 'react'
import { Modal, ModalBody, Button } from 'reactstrap'
import { connect } from 'react-redux'
import intl from 'react-intl-universal'
import { useEmbed, Statuses } from '~/reactapp/shared/use-embed'
import { IEllipsisReduxStore } from '~/redux/types'
import { CustomEmbed } from '~/reactapp/story/ans/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./CustomEmbedModal.styles.scss')

interface StoreProps {
  customEmbedTypes: IEllipsisReduxStore['settings']['customEmbed']
}

export interface Props {
  visible: boolean
  mode: 'search' | 'edit'
  data?: CustomEmbed['embed']
  onClose: () => void
  onData: (data: CustomEmbed['embed']) => void
  /**
   * Custom embed subtype configured in DynamoDB
   * https://github.com/WPMedia/arc-story-builder-ui/blob/master/dev/dynamo/data/ellipsis-settings-server/data/0001.json#L11
   */
  subtype: string
}

export function CustomEmbedModal(props: Props & StoreProps) {
  const { customEmbedTypes, onClose, onData, subtype, visible, mode } = props
  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const customEmbedConfig = customEmbedTypes[subtype]
  if (!customEmbedConfig) {
    throw new Error(
      `No configuration set up for custom embed ${subtype} subtype`
    )
  }
  const {
    status,
    messageData,
    retry,
    unsubscribe,
    url,
    actionData,
    actionCancel,
  } = useEmbed({
    timeoutMs: customEmbedConfig.loadTimeout,
    baseUrl: customEmbedConfig[mode],
    data: props.data,
    iframeElem: iframeRef,
  })

  React.useEffect(() => {
    if (messageData && messageData.action === actionData) {
      unsubscribe()
      onData(messageData.data as CustomEmbed['embed'])
    } else if (messageData && messageData.action === actionCancel) {
      unsubscribe()
      onClose()
    }
  }, [onData, messageData, unsubscribe, onClose, actionData, actionCancel])

  const title = intl.get(`CustomEmbedModal.${mode}`)

  return (
    <Modal
      wrapClassName="bootstrap4"
      size="xl"
      className={styles.root}
      centered
      isOpen={visible}
      toggle={onClose}
    >
      <ModalBody>
        {status === Statuses.Timeout ? (
          <React.Fragment>
            <p>{intl.get('CustomEmbedModal.integrationLoadTimeout')}</p>
            <Button color="primary" onClick={retry}>
              {intl.get('CustomEmbedModal.retry')}
            </Button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {status === Statuses.Loading && (
              <p>{intl.get('CustomEmbedModal.loading')}</p>
            )}
            <iframe
              title={title}
              height="100%"
              width="100%"
              src={url}
              ref={iframeRef}
            />
          </React.Fragment>
        )}
      </ModalBody>
    </Modal>
  )
}

export default connect((state: IEllipsisReduxStore) => ({
  customEmbedTypes: state.settings.customEmbed,
}))(CustomEmbedModal)
